import { useState, useEffect, useRef } from "react";

/* ─── DATA ─────────────────────────────────────────────── */
const FEATURES = [
  {
    icon: "◈",
    title: "Session Tracking",
    desc: "Monitor every bill moving through the 103rd Michigan Legislature in real time — with status updates, action history, and committee referrals across all 3,200+ bills this session.",
  },
  {
    icon: "⬡",
    title: "Bill Search & Analysis",
    desc: "Search the full session by keyword, sponsor, chamber, or status. Track bills for your clients, assign positions, and follow legislation from introduction to final disposition.",
  },
  {
    icon: "◎",
    title: "Bill Studio",
    desc: "Upload any Michigan bill and get an instant plain-language summary, key stakeholder analysis, fiscal implications, and a breakdown of how the legislation would function in practice.",
  },
  {
    icon: "⬙",
    title: "Client Portal",
    desc: "Manage your entire book of business in one place — track bills by client, log meetings and activity, and generate professional client reports with a single click.",
  },
  {
    icon: "▣",
    title: "Campaign Finance",
    desc: "Search Michigan campaign finance records across candidates, PACs, and committees. Understand the financial relationships shaping legislative priorities.",
  },
  {
    icon: "◆",
    title: "Directory",
    desc: "A comprehensive directory of Michigan state agencies, sub-agencies, lobbying firms, lobbyists, and associations — all in one searchable, organized resource.",
  },
];

const USE_CASES = [
  { label: "Trade Associations", icon: "🏛" },
  { label: "Lobbying Firms", icon: "⚖" },
  { label: "Political Consultants", icon: "🗳" },
  { label: "Corporate Gov Affairs", icon: "🏢" },
  { label: "Regulatory Law Firms", icon: "📋" },
  { label: "PACs & Political Orgs", icon: "🎯" },
];

const BENEFITS = [
  "Track every bill across the full legislative session",
  "Manage clients, meetings, and activity in one platform",
  "Search campaign finance data across all Michigan candidates",
  "Generate professional client reports in seconds",
  "Monitor committee activity and floor votes in real time",
  "Access a comprehensive Michigan government affairs directory",
];

const DEMO_QUESTIONS = [
  "Summarize HB 4482 and identify key stakeholders.",
  "What are the top donors to the House Commerce Committee chairs?",
  "Show me all active bills related to healthcare this session.",
  "Generate a client activity report for April 2026.",
];

/* ─── HELPERS ───────────────────────────────────────────── */
function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

function FadeIn({ children, delay = 0, className = "", style = {} }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? "translateY(0)" : "translateY(24px)",
      transition: `opacity 0.75s ease ${delay}s, transform 0.75s ease ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
}

/* ─── DEMO WIDGET ───────────────────────────────────────── */
function DemoWidget() {
  const [active, setActive] = useState(0);
  const [typing, setTyping] = useState(false);
  const [shown, setShown] = useState("");
  const [result, setResult] = useState(false);

  const runDemo = (idx) => {
    setActive(idx);
    setResult(false);
    setShown("");
    setTyping(true);
    const q = DEMO_QUESTIONS[idx];
    let i = 0;
    const iv = setInterval(() => {
      i++;
      setShown(q.slice(0, i));
      if (i >= q.length) { clearInterval(iv); setTyping(false); setTimeout(() => setResult(true), 400); }
    }, 22);
  };

  useEffect(() => { runDemo(0); }, []);

  return (
    <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, overflow: "hidden", fontFamily: "var(--font-mono)" }}>
      {/* window chrome */}
      <div style={{ background: "var(--surface2)", padding: "0.75rem 1.25rem", display: "flex", alignItems: "center", gap: "0.5rem", borderBottom: "1px solid var(--border)" }}>
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#ff5f56", display: "inline-block" }} />
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#ffbd2e", display: "inline-block" }} />
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#27c93f", display: "inline-block" }} />
        <span style={{ marginLeft: "0.5rem", fontSize: "0.72rem", color: "var(--muted)", letterSpacing: "0.08em" }}>WhipCount™ — Platform Preview</span>
      </div>

      {/* query buttons */}
      <div style={{ padding: "1rem 1.25rem 0", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
        {DEMO_QUESTIONS.map((q, i) => (
          <button key={i} onClick={() => runDemo(i)} style={{
            background: active === i ? "var(--gold)" : "var(--surface2)",
            color: active === i ? "#0a0a0a" : "var(--muted)",
            border: "1px solid " + (active === i ? "var(--gold)" : "var(--border)"),
            borderRadius: 6, padding: "0.3rem 0.75rem",
            fontSize: "0.72rem", cursor: "pointer", fontFamily: "var(--font-mono)",
            transition: "all 0.2s", fontWeight: active === i ? 700 : 400,
          }}>Q{i + 1}</button>
        ))}
      </div>

      {/* input area */}
      <div style={{ padding: "1rem 1.25rem 0" }}>
        <div style={{ fontSize: "0.7rem", color: "var(--gold)", marginBottom: "0.4rem", letterSpacing: "0.1em" }}>▸ USER INPUT</div>
        <div style={{
          background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8,
          padding: "0.85rem 1rem", fontSize: "0.82rem", color: "var(--text)",
          minHeight: 56, lineHeight: 1.5,
        }}>
          {shown}{typing && <span style={{ animation: "blink 1s infinite", opacity: 1 }}>|</span>}
        </div>
      </div>

      {/* output area */}
      <div style={{ padding: "1rem 1.25rem 1.25rem" }}>
        <div style={{ fontSize: "0.7rem", color: "var(--slate)", marginBottom: "0.4rem", letterSpacing: "0.1em" }}>▸ WHIPCOUNT ANALYSIS</div>
        <div style={{
          background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8,
          padding: "0.85rem 1rem", fontSize: "0.82rem", color: "var(--muted)",
          minHeight: 110, lineHeight: 1.6,
          transition: "opacity 0.4s",
          opacity: result ? 1 : 0,
        }}>
          {result && active === 0 && <>
            <span style={{ color: "var(--gold)", fontWeight: 700 }}>HB 4482 — Motor Vehicle Dealer Licensing Reform</span><br />
            <span style={{ color: "var(--text)" }}>Amends dealer licensing requirements under the Occupational Code.</span> Currently In Progress — advanced out of House Commerce Committee Apr 21. Key stakeholders include Michigan Auto Dealers Association (support) and independent dealer groups. Fiscal note: minimal cost to the state, estimated $220K in licensing fee revenue annually.
          </>}
          {result && active === 1 && <>
            <span style={{ color: "var(--gold)", fontWeight: 700 }}>House Commerce Committee — Top Donors</span><br />
            <span style={{ color: "var(--text)" }}>Michigan Chamber of Commerce PAC ($485K), Small Business Association PAC ($312K), Michigan Retailers Association ($198K).</span> Committee donor profile is heavily business and trade-aligned. Bills with strong industry backing have moved quickly through this committee in the current session.
          </>}
          {result && active === 2 && <>
            <span style={{ color: "var(--gold)", fontWeight: 700 }}>Active Healthcare Bills — 103rd Legislature</span><br />
            47 bills currently active with healthcare-related subject matter. 12 are In Progress, 28 are In Committee, 7 are Introduced. Top active bills include SB 0341 (EV infrastructure tax credit with healthcare carve-out), HB 4891 (lemon law amendments with provider provisions), and HB 4203 (disclosure requirements). Filter by chamber or committee in Bill Search for full results.
          </>}
          {result && active === 3 && <>
            <span style={{ color: "var(--gold)", fontWeight: 700 }}>Client Report — April 2026</span><br />
            Michigan Auto Dealers Association · 14 tracked bills · 6 meetings · 24.5 hours logged. Three priority bills advanced this month. Key win: SB 0341 cleared Senate Transportation Committee 7-2 following written testimony submitted Apr 19. Priority for May: delay floor vote on HB 4891 and secure substitute language limiting dealer liability. Report ready to export as PDF.
          </>}
        </div>
      </div>
    </div>
  );
}

/* ─── MAIN COMPONENT ────────────────────────────────────── */
export default function WhipCount({ onNavigate }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [formState, setFormState] = useState({ name: "", org: "", email: "", role: "", message: "" });
  const [sent, setSent] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTos, setShowTos] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=IBM+Plex+Sans:wght@300;400;500&family=IBM+Plex+Mono:wght@400;500&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { scroll-behavior: smooth; }

    :root {
      --bg: #09090e;
      --surface: #111118;
      --surface2: #191921;
      --charcoal: #1e1e28;
      --gold: #c9a84c;
      --gold-dim: #a07830;
      --gold-glow: rgba(201,168,76,0.15);
      --blue: #2d6fe0;
      --slate: #4a7fa5;
      --text: #e6e4dc;
      --muted: #7a7870;
      --border: rgba(255,255,255,0.06);
      --font-display: 'Playfair Display', serif;
      --font-body: 'IBM Plex Sans', sans-serif;
      --font-mono: 'IBM Plex Mono', monospace;
    }

    body { background: var(--bg); color: var(--text); font-family: var(--font-body); -webkit-font-smoothing: antialiased; }

    @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
    @keyframes fadeDown { from{opacity:0;transform:translateY(-16px)} to{opacity:1;transform:translateY(0)} }
    @keyframes pulse { 0%,100%{opacity:0.5;transform:scale(1)} 50%{opacity:1;transform:scale(1.04)} }
    @keyframes scanline { 0%{top:-30%} 100%{top:130%} }

    /* NAV */
    .nav {
      position: fixed; top:0; left:0; right:0; z-index:100;
      display:flex; align-items:center; justify-content:space-between;
      padding:1.1rem 5vw;
      transition: background 0.4s, border-color 0.4s;
    }
    .nav.scrolled {
      background: rgba(9,9,14,0.9);
      backdrop-filter: blur(16px);
      border-bottom: 1px solid var(--border);
    }
    .nav-logo {
      font-family: var(--font-display); font-size:1.3rem; font-weight:800;
      color:var(--text); cursor:pointer; letter-spacing:-0.01em;
    }
    .nav-logo em { font-style:italic; color:var(--gold); }
    .nav-logo sup { font-family:var(--font-body); font-size:0.55rem; color:var(--muted); font-weight:400; vertical-align:super; margin-left:1px; }
    .nav-links { display:flex; gap:2rem; list-style:none; }
    .nav-links button {
      background:none; border:none; color:var(--muted);
      font-family:var(--font-body); font-size:0.82rem; font-weight:500;
      cursor:pointer; letter-spacing:0.1em; text-transform:uppercase;
      transition:color 0.2s;
    }
    .nav-links button:hover { color:var(--text); }
    .nav-cta {
      background:transparent; color:var(--gold);
      border:1px solid var(--gold); border-radius:4px;
      padding:0.5rem 1.25rem;
      font-family:var(--font-body); font-size:0.82rem; font-weight:500;
      cursor:pointer; letter-spacing:0.06em;
      transition: background 0.2s, color 0.2s;
    }
    .nav-cta:hover { background:var(--gold); color:#09090e; }
    .hamburger { display:none; background:none; border:none; color:var(--text); font-size:1.4rem; cursor:pointer; }

    /* HERO */
    .hero {
      min-height: 100vh; padding: 9rem 5vw 6rem;
      display:flex; align-items:center;
      position:relative; overflow:hidden;
    }
    .hero-noise {
      position:absolute; inset:0; z-index:0;
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.035'/%3E%3C/svg%3E");
      opacity: 0.6;
    }
    .hero-glow {
      position:absolute; z-index:0;
      width:700px; height:700px;
      border-radius:50%;
      background: radial-gradient(circle, rgba(201,168,76,0.07) 0%, transparent 70%);
      top:-200px; right:-200px;
      pointer-events:none;
    }
    .hero-glow2 {
      position:absolute; z-index:0;
      width:500px; height:500px;
      border-radius:50%;
      background: radial-gradient(circle, rgba(45,111,224,0.06) 0%, transparent 70%);
      bottom:-100px; left:-100px;
      pointer-events:none;
    }
    .hero-grid {
      position:absolute; inset:0; z-index:0;
      background-image:
        linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
      background-size:80px 80px;
      mask-image: radial-gradient(ellipse 70% 80% at 60% 40%, black 20%, transparent 80%);
    }
    .hero-inner {
      position:relative; z-index:1;
      display:grid; grid-template-columns:1fr 1fr;
      gap:5rem; align-items:center;
      max-width:1200px; margin:0 auto; width:100%;
    }
    .hero-eyebrow {
      display:inline-flex; align-items:center; gap:0.6rem;
      font-family:var(--font-mono); font-size:0.7rem;
      color:var(--gold); letter-spacing:0.15em; text-transform:uppercase;
      margin-bottom:1.5rem;
      animation: fadeDown 0.6s ease both;
    }
    .hero-eyebrow-dot {
      width:6px; height:6px; border-radius:50%; background:var(--gold);
      animation: pulse 2s infinite;
    }
    .hero h1 {
      font-family:var(--font-display);
      font-size: clamp(2.6rem, 4.5vw, 4rem);
      font-weight:900; line-height:1.08;
      letter-spacing:-0.02em; color:var(--text);
      margin-bottom:1.5rem;
      animation: fadeDown 0.6s 0.08s ease both;
    }
    .hero h1 em {
      font-style:italic; color:var(--gold);
    }
    .hero-sub {
      font-size:1.02rem; color:var(--muted); line-height:1.75;
      font-weight:300; margin-bottom:2.5rem; max-width:460px;
      animation: fadeDown 0.6s 0.16s ease both;
    }
    .hero-actions {
      display:flex; gap:1rem; flex-wrap:wrap;
      animation: fadeDown 0.6s 0.24s ease both;
    }
    .btn-gold {
      background:var(--gold); color:#09090e;
      border:none; border-radius:4px;
      padding:0.85rem 1.75rem;
      font-family:var(--font-body); font-size:0.95rem; font-weight:500;
      cursor:pointer; letter-spacing:0.03em;
      transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
    }
    .btn-gold:hover { background:#d4b05a; transform:translateY(-2px); box-shadow:0 8px 28px var(--gold-glow); }
    .btn-outline {
      background:transparent; color:var(--text);
      border:1px solid var(--border); border-radius:4px;
      padding:0.85rem 1.75rem;
      font-family:var(--font-body); font-size:0.95rem; font-weight:400;
      cursor:pointer;
      transition: border-color 0.2s, color 0.2s, transform 0.15s;
    }
    .btn-outline:hover { border-color:var(--gold); color:var(--gold); transform:translateY(-2px); }

    /* DIVIDER */
    .divider {
      border:none;
      height:1px;
      background:linear-gradient(90deg, transparent, var(--border), transparent);
      margin:0;
    }

    /* TICKER */
    .ticker {
      background:var(--surface); border-top:1px solid var(--border); border-bottom:1px solid var(--border);
      padding:1rem 0; overflow:hidden; position:relative;
    }
    .ticker-inner {
      display:flex; gap:3rem; width:max-content;
      animation: ticker 28s linear infinite;
    }
    @keyframes ticker {
      from { transform:translateX(0) } to { transform:translateX(-50%) }
    }
    .ticker-item {
      display:flex; align-items:center; gap:0.5rem;
      font-family:var(--font-mono); font-size:0.75rem;
      color:var(--muted); white-space:nowrap;
    }
    .ticker-item strong { color:var(--gold); }
    .ticker-sep { color:var(--border); }

    /* SECTIONS */
    .section { padding:7rem 5vw; }
    .section-inner { max-width:1150px; margin:0 auto; }
    .section-tag {
      display:inline-block;
      font-family:var(--font-mono); font-size:0.68rem;
      color:var(--gold); letter-spacing:0.15em; text-transform:uppercase;
      margin-bottom:1rem;
      padding-bottom:0.4rem;
      border-bottom:1px solid var(--gold-dim);
    }
    .section-title {
      font-family:var(--font-display);
      font-size:clamp(1.9rem, 3.5vw, 2.8rem);
      font-weight:800; letter-spacing:-0.02em; line-height:1.12;
      margin-bottom:1.1rem; color:var(--text);
    }
    .section-sub {
      font-size:1rem; color:var(--muted); max-width:500px;
      line-height:1.75; font-weight:300;
    }

    /* FEATURES */
    .features-bg { background:var(--surface); }
    .features-grid {
      display:grid; grid-template-columns:repeat(3,1fr);
      gap:1px; margin-top:4rem;
      border:1px solid var(--border); border-radius:12px; overflow:hidden;
    }
    .feature-card {
      background:var(--bg); padding:2.25rem 1.75rem;
      transition:background 0.25s;
      position:relative;
    }
    .feature-card:hover { background:var(--charcoal); }
    .feature-icon {
      display:inline-block; font-size:1.5rem; color:var(--gold);
      margin-bottom:1.1rem; line-height:1;
    }
    .feature-title {
      font-family:var(--font-display); font-size:1.1rem;
      font-weight:700; letter-spacing:-0.01em;
      margin-bottom:0.7rem; color:var(--text);
    }
    .feature-desc { font-size:0.87rem; color:var(--muted); line-height:1.65; font-weight:300; }

    /* DEMO SECTION */
    .demo-layout {
      display:grid; grid-template-columns:1fr 1.1fr;
      gap:5rem; align-items:start; margin-top:4rem;
    }

    /* BENEFITS */
    .benefits-bg { background:var(--surface); }
    .benefits-layout {
      display:grid; grid-template-columns:1fr 1fr;
      gap:5rem; align-items:center; margin-top:3.5rem;
    }
    .benefits-list { list-style:none; display:flex; flex-direction:column; gap:1rem; }
    .benefits-list li {
      display:flex; align-items:flex-start; gap:1rem;
      font-size:0.95rem; color:var(--text); line-height:1.55;
    }
    .benefit-check {
      min-width:22px; height:22px; border-radius:4px;
      background:var(--gold-glow); border:1px solid var(--gold-dim);
      display:flex; align-items:center; justify-content:center;
      font-size:0.72rem; color:var(--gold); font-weight:700;
      flex-shrink:0; margin-top:1px;
    }
    .customers-grid {
      display:grid; grid-template-columns:1fr 1fr; gap:0.75rem;
    }
    .customer-pill {
      display:flex; align-items:center; gap:0.75rem;
      background:var(--bg); border:1px solid var(--border);
      border-radius:8px; padding:0.85rem 1rem;
      font-size:0.85rem; color:var(--muted);
      transition:border-color 0.2s, color 0.2s;
    }
    .customer-pill:hover { border-color:var(--gold-dim); color:var(--text); }
    .customer-pill span { font-size:1.1rem; }

    /* CONTACT */
    .contact-layout {
      display:grid; grid-template-columns:1fr 1.3fr;
      gap:5rem; margin-top:4rem; align-items:start;
    }
    .contact-sidebar h3 {
      font-family:var(--font-display); font-size:1.4rem;
      font-weight:800; letter-spacing:-0.02em; color:var(--text); margin-bottom:0.75rem;
    }
    .contact-sidebar p { font-size:0.9rem; color:var(--muted); line-height:1.7; font-weight:300; margin-bottom:1.75rem; }
    .contact-note {
      background:var(--charcoal); border-left:3px solid var(--gold);
      border-radius:0 6px 6px 0; padding:1rem 1.25rem;
      font-size:0.8rem; color:var(--muted); line-height:1.6;
    }
    .contact-note strong { color:var(--text); }
    .form { display:flex; flex-direction:column; gap:1.1rem; }
    .form-row { display:grid; grid-template-columns:1fr 1fr; gap:1.1rem; }
    .field { display:flex; flex-direction:column; gap:0.4rem; }
    .field label {
      font-family:var(--font-mono); font-size:0.68rem;
      color:var(--muted); letter-spacing:0.1em; text-transform:uppercase;
    }
    .field input, .field textarea, .field select {
      background:var(--surface); border:1px solid var(--border);
      border-radius:6px; padding:0.75rem 1rem;
      color:var(--text); font-family:var(--font-body); font-size:0.9rem;
      outline:none; transition:border-color 0.2s; resize:none;
      appearance:none;
    }
    .field input:focus, .field textarea:focus, .field select:focus { border-color:var(--gold-dim); }
    .field input::placeholder, .field textarea::placeholder { color:var(--muted); }
    .field select option { background:var(--surface); }
    .sent-msg {
      background:rgba(201,168,76,0.08); border:1px solid var(--gold-dim);
      border-radius:10px; padding:2rem; text-align:center;
      color:var(--gold); font-weight:500; font-family:var(--font-mono); font-size:0.9rem;
      line-height:1.7;
    }

    /* FOOTER */
    footer {
      border-top:1px solid var(--border);
      padding:3rem 5vw 2rem;
    }
    .footer-grid {
      display:grid; grid-template-columns:1.4fr 1fr 1fr; gap:2.5rem;
      margin-bottom:2.5rem;
    }
    .footer-col-label {
      font-size:0.6rem; font-weight:700; letter-spacing:0.14em;
      text-transform:uppercase; color:var(--gold);
      font-family:var(--font-mono); margin-bottom:0.85rem;
    }
    .footer-logo { font-family:var(--font-display); font-size:1.25rem; font-weight:800; color:var(--text); margin-bottom:0.4rem; }
    .footer-logo em { font-style:italic; color:var(--gold); }
    .footer-logo sup { font-family:var(--font-body); font-size:0.45rem; color:var(--muted); font-weight:400; vertical-align:super; margin-left:1px; }
    .footer-tagline { font-size:0.75rem; color:var(--muted); font-family:var(--font-mono); line-height:1.6; max-width:280px; }
    .footer-contact-line { display:flex; align-items:center; gap:0.5rem; font-size:0.78rem; color:var(--muted); font-family:var(--font-mono); margin-bottom:0.45rem; }
    .footer-contact-line a { color:var(--muted); text-decoration:none; transition:color 0.15s; }
    .footer-contact-line a:hover { color:var(--gold); }
    .footer-nav { display:flex; flex-direction:column; gap:0.5rem; }
    .footer-nav a, .footer-nav button {
      background:none; border:none; padding:0; text-align:left;
      font-size:0.78rem; color:var(--muted); font-family:var(--font-mono);
      cursor:pointer; text-decoration:none; transition:color 0.15s;
    }
    .footer-nav a:hover, .footer-nav button:hover { color:var(--gold); }
    .footer-bottom {
      display:flex; flex-wrap:wrap; align-items:center;
      justify-content:space-between; gap:0.75rem;
      padding-top:1.5rem; border-top:1px solid var(--border);
    }
    .footer-copy { font-size:0.68rem; color:var(--muted); font-family:var(--font-mono); opacity:0.55; }
    .footer-bottom-links { display:flex; gap:0.75rem; flex-wrap:wrap; }
    .footer-bottom-links a, .footer-bottom-links button {
      background:none; border:none; padding:0; cursor:pointer;
      font-size:0.68rem; color:var(--muted); font-family:var(--font-mono);
      text-decoration:none; opacity:0.55; transition:opacity 0.15s, color 0.15s;
    }
    .footer-bottom-links a:hover, .footer-bottom-links button:hover { opacity:1; color:var(--gold); }
    .footer-sep { font-size:0.68rem; color:var(--border); opacity:0.5; user-select:none; font-family:var(--font-mono); }
    .footer-disclaimer {
      width:100%; font-size:0.66rem; color:var(--muted);
      font-family:var(--font-mono); line-height:1.6;
      margin-top:1rem; opacity:0.4;
    }
    @media(max-width:700px){
      .footer-grid { grid-template-columns:1fr; gap:1.75rem; }
      .footer-bottom { flex-direction:column; align-items:flex-start; }
    }

    /* HOMEPAGE PRIVACY MODAL */
    .hp-privacy-overlay {
      position:fixed; inset:0; background:rgba(0,0,0,0.75);
      backdrop-filter:blur(4px); z-index:1000;
      display:flex; align-items:center; justify-content:center; padding:1.5rem;
    }
    .hp-privacy-modal {
      background:var(--surface); border:1px solid var(--border);
      border-radius:12px; width:100%; max-width:680px;
      max-height:85vh; display:flex; flex-direction:column; overflow:hidden;
    }
    .hp-privacy-head {
      display:flex; align-items:center; justify-content:space-between;
      padding:1.25rem 1.5rem; border-bottom:1px solid var(--border); flex-shrink:0;
    }
    .hp-privacy-title { font-size:0.9rem; font-weight:700; color:var(--text); font-family:var(--font-display); }
    .hp-privacy-updated { font-size:0.62rem; color:var(--muted); font-family:var(--font-mono); margin-top:2px; opacity:0.6; }
    .hp-privacy-close {
      background:none; border:1px solid var(--border); border-radius:6px;
      padding:0.3rem 0.65rem; cursor:pointer; font-size:0.75rem;
      color:var(--muted); transition:all 0.15s;
    }
    .hp-privacy-close:hover { border-color:var(--gold); color:var(--gold); }
    .hp-privacy-body { padding:1.5rem; overflow-y:auto; flex:1; scrollbar-width:thin; scrollbar-color:var(--border) transparent; }
    .hp-privacy-body h2 {
      font-size:0.68rem; font-weight:700; letter-spacing:0.12em;
      text-transform:uppercase; color:var(--gold);
      font-family:var(--font-mono); margin:1.5rem 0 0.5rem;
      border-bottom:1px solid var(--border); padding-bottom:0.3rem;
    }
    .hp-privacy-body h2:first-child { margin-top:0; }
    .hp-privacy-body p, .hp-privacy-body li {
      font-size:0.8rem; color:var(--muted); line-height:1.75; margin-bottom:0.5rem;
      font-family:var(--font-mono);
    }
    .hp-privacy-body ul { padding-left:1.25rem; margin-bottom:0.5rem; }
    .hp-privacy-body strong { color:var(--text); font-weight:600; }
    .hp-privacy-body a { color:var(--gold); text-decoration:none; }
    .hp-privacy-disclaimer {
      margin-top:1rem; padding:0.75rem 1rem;
      background:var(--surface2); border-radius:8px;
      font-size:0.68rem; color:var(--muted); line-height:1.6; font-family:var(--font-mono);
    }
    .hp-privacy-foot {
      padding:1rem 1.5rem; border-top:1px solid var(--border);
      display:flex; justify-content:flex-end; flex-shrink:0;
    }
    .hp-privacy-foot button {
      background:var(--gold); color:#0a0a0a; border:none;
      border-radius:8px; padding:0.55rem 1.25rem;
      font-size:0.78rem; font-weight:700; cursor:pointer;
      font-family:var(--font-mono); transition:opacity 0.15s;
    }
    .hp-privacy-foot button:hover { opacity:0.85; }

    /* MOBILE */
    .mobile-menu {
      position:fixed; inset:0; background:var(--bg); z-index:200;
      display:flex; flex-direction:column; align-items:center; justify-content:center; gap:2rem;
    }
    .mobile-menu button {
      background:none; border:none; color:var(--text);
      font-family:var(--font-display); font-size:1.8rem; font-weight:700; cursor:pointer;
    }
    .mobile-close {
      position:absolute; top:1.5rem; right:1.5rem;
      background:none; border:none; color:var(--muted); font-size:1.8rem; cursor:pointer;
    }

    @media(max-width:900px) {
      .nav-links,.nav-cta { display:none; }
      .hamburger { display:block; }
      .hero-inner { grid-template-columns:1fr; }
      .features-grid { grid-template-columns:1fr; }
      .demo-layout,.benefits-layout,.contact-layout { grid-template-columns:1fr; }
      .form-row { grid-template-columns:1fr; }
      .customers-grid { grid-template-columns:1fr; }
    }
  `;

  const TICKER_ITEMS = [
    { label: "Legislator Models", value: "148" },
    { label: "MI House Members", value: "110" },
    { label: "MI Senate Members", value: "38" },
    { label: "Data Sources", value: "12+" },
    { label: "Prediction Accuracy", value: "High Confidence" },
    { label: "Platform Access", value: "Secure / Enterprise" },
    { label: "Refresh Cycle", value: "Continuous" },
  ];

  return (
    <>
      <style>{css}</style>

      {menuOpen && (
        <div className="mobile-menu">
          <button className="mobile-close" onClick={() => setMenuOpen(false)}>✕</button>
          {["Platform", "Demo", "Clients", "Contact"].map(l => (
            <button key={l} onClick={() => scrollTo(l.toLowerCase())}>{l}</button>
          ))}
          <button onClick={() => onNavigate && onNavigate("login")} style={{ color:"var(--gold)" }}>Sign In / Sign Up →</button>
        </div>
      )}

      {/* NAV */}
      <nav className={`nav${scrolled ? " scrolled" : ""}`}>
        <div className="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          Whip<em>Count</em><sup>™</sup>
        </div>
        <ul className="nav-links">
          {["Platform", "Demo", "Clients", "Contact"].map(l => (
            <li key={l}><button onClick={() => scrollTo(l.toLowerCase())}>{l}</button></li>
          ))}
        </ul>
        <div style={{display:"flex",gap:"0.75rem",alignItems:"center"}}>
          <button className="nav-cta" style={{background:"transparent",color:"var(--gold)",border:"1px solid var(--gold-dim)"}} onClick={() => onNavigate && onNavigate("subscribe")}>Subscribe</button>
          <button className="nav-cta" onClick={() => onNavigate && onNavigate("login")}>Sign In / Sign Up →</button>
        </div>
        <button className="hamburger" onClick={() => setMenuOpen(true)}>☰</button>
      </nav>

      {/* HERO */}
      <section className="hero">
        <div className="hero-noise" />
        <div className="hero-grid" />
        <div className="hero-glow" />
        <div className="hero-glow2" />
        <div className="hero-inner">
          <div>
            <div className="hero-eyebrow">
              <div className="hero-eyebrow-dot" />
              Michigan Legislative Intelligence Platform
            </div>
            <h1>
              Walk in the room<br />
              <em>fully prepared.</em>
            </h1>
            <p className="hero-sub">
              WhipCount™ gives Michigan lobbyists and government affairs professionals everything they need to stay ahead — bill tracking, client management, campaign finance data, legislative intelligence, and a full government affairs directory — all in one platform.
            </p>
            <div className="hero-actions">
              <button className="btn-gold" onClick={() => scrollTo("contact")}>Request Access</button>
              <button className="btn-outline" onClick={() => onNavigate && onNavigate("subscribe")}>View Pricing →</button>
              <button className="btn-outline" onClick={() => scrollTo("demo")}>See Live Demo</button>
            </div>
          </div>
          <FadeIn delay={0.2}>
            <DemoWidget />
          </FadeIn>
        </div>
      </section>

      {/* TICKER */}
      <div className="ticker">
        <div className="ticker-inner">
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => (
            <div className="ticker-item" key={i}>
              <strong>{item.value}</strong>
              <span>{item.label}</span>
              {i < TICKER_ITEMS.length * 2 - 1 && <span className="ticker-sep">—</span>}
            </div>
          ))}
        </div>
      </div>

      {/* PLATFORM / FEATURES */}
      <section id="platform" className="section features-bg">
        <div className="section-inner">
          <FadeIn>
            <span className="section-tag">Platform Features</span>
            <h2 className="section-title">Everything you need to run<br />a Michigan government affairs practice.</h2>
            <p className="section-sub">Six integrated tools — built for lobbyists, associations, and government affairs professionals who need real intelligence, not spreadsheets.</p>
          </FadeIn>
          <div className="features-grid">
            {FEATURES.map(({ icon, title, desc }, i) => (
              <FadeIn key={title} delay={i * 0.07} className="feature-card">
                <span className="feature-icon">{icon}</span>
                <div className="feature-title">{title}</div>
                <div className="feature-desc">{desc}</div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <hr className="divider" />

      <hr className="divider" />

      {/* CLIENTS / BENEFITS */}
      <section id="clients" className="section benefits-bg">
        <div className="section-inner">
          <FadeIn>
            <span className="section-tag">Built For</span>
            <h2 className="section-title">Decision-support for<br />professionals who can't afford to guess.</h2>
          </FadeIn>
          <div className="benefits-layout">
            <FadeIn delay={0.05}>
              <ul className="benefits-list">
                {BENEFITS.map((b, i) => (
                  <li key={i}>
                    <div className="benefit-check">✓</div>
                    {b}
                  </li>
                ))}
              </ul>
            </FadeIn>
            <FadeIn delay={0.1}>
              <p style={{ fontSize: "0.85rem", color: "var(--muted)", marginBottom: "1.5rem", fontWeight: 300, lineHeight: 1.7 }}>
                WhipCount™ is built for professionals who live and work inside the Michigan legislative process — replacing scattered spreadsheets, stale PDFs, and siloed tools with a single intelligent platform.
              </p>
              <div className="customers-grid">
                {USE_CASES.map(({ label, icon }) => (
                  <div className="customer-pill" key={label}>
                    <span>{icon}</span> {label}
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <hr className="divider" />

      {/* CONTACT */}
      <section id="contact" className="section">
        <div className="section-inner">
          <FadeIn>
            <span className="section-tag">Request Access</span>
            <h2 className="section-title">Platform access is<br />available to qualified professionals.</h2>
          </FadeIn>
          <div className="contact-layout">
            <FadeIn delay={0.05}>
              <div className="contact-sidebar">
                <h3>Get in touch</h3>
                <p>
                  WhipCount™ is offered as an enterprise platform. Fill out the form and our team will reach out to discuss your organization's needs, access tiers, and onboarding.
                </p>
                <div className="contact-note">
                  <strong>Platform note:</strong> WhipCount™ is a decision-support tool. All predictions represent probability estimates derived from public data. Results should be evaluated alongside professional judgment and legal counsel where applicable.
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.1}>
              {sent ? (
                <div className="sent-msg">
                  ✓ ACCESS REQUEST RECEIVED<br /><br />
                  Our team will review your submission and be in touch within one business day to discuss platform access and onboarding.
                </div>
              ) : (
                <form className="form" onSubmit={async e => {
                  e.preventDefault();
                  try {
                    await fetch("https://legisim-server.onrender.com/api/subscribe", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        firstName: formState.name.split(" ")[0] || formState.name,
                        lastName: formState.name.split(" ").slice(1).join(" ") || "",
                        email: formState.email,
                        org: formState.org,
                        role: formState.role,
                        phone: "",
                        message: formState.message,
                        source: "homepage"
                      })
                    });
                  } catch {}
                  setSent(true);
                }}>
                  <div className="form-row">
                    <div className="field">
                      <label>Full Name</label>
                      <input type="text" placeholder="Jane Smith" required value={formState.name} onChange={e => setFormState(s => ({ ...s, name: e.target.value }))} />
                    </div>
                    <div className="field">
                      <label>Organization</label>
                      <input type="text" placeholder="Firm or Association" required value={formState.org} onChange={e => setFormState(s => ({ ...s, org: e.target.value }))} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="field">
                      <label>Work Email</label>
                      <input type="email" placeholder="jane@org.com" required value={formState.email} onChange={e => setFormState(s => ({ ...s, email: e.target.value }))} />
                    </div>
                    <div className="field">
                      <label>Role</label>
                      <select value={formState.role} onChange={e => setFormState(s => ({ ...s, role: e.target.value }))} required>
                        <option value="">Select role...</option>
                        <option>Lobbyist / Government Affairs</option>
                        <option>Trade Association</option>
                        <option>Political Consultant</option>
                        <option>Corporate Affairs</option>
                        <option>Law Firm</option>
                        <option>PAC / Political Organization</option>
                        <option>Academic / Research</option>
                        <option>Media</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="field">
                    <label>Tell us about your use case</label>
                    <textarea rows={4} placeholder="Describe the legislation, issue area, or workflow you'd like to model..." value={formState.message} onChange={e => setFormState(s => ({ ...s, message: e.target.value }))} />
                  </div>
                  <button type="submit" className="btn-gold" style={{ alignSelf: "flex-start" }}>
                    Submit Access Request →
                  </button>
                </form>
              )}
            </FadeIn>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer>
        <div className="footer-grid">

          {/* Brand */}
          <div>
            <div className="footer-col-label">Platform</div>
            <div className="footer-logo">Whip<em>Count</em><sup>™</sup></div>
            <div className="footer-tagline">Your one-stop platform for Michigan government affairs — everything a lobbyist, association, or public affairs professional needs to track legislation, manage clients, and stay ahead of the session.</div>
          </div>

          {/* Contact */}
          <div>
            <div className="footer-col-label">Contact</div>
            <div className="footer-contact-line">
              <span style={{opacity:0.5}}>✉</span>
              <a href="mailto:admin@whipcount.us">admin@whipcount.us</a>
            </div>
            <div className="footer-contact-line" style={{marginTop:"0.25rem",opacity:0.55,fontSize:"0.72rem"}}>
              Mon – Fri &nbsp;·&nbsp; 9:00 AM – 5:00 PM ET
            </div>
          </div>

          {/* Legal */}
          <div>
            <div className="footer-col-label">Legal</div>
            <div className="footer-nav">
              <button onClick={() => setShowPrivacy(true)}>Privacy Policy</button>
              <button onClick={() => setShowTos(true)}>Terms of Service</button>
              <a href="https://legiscan.com" target="_blank" rel="noreferrer">Data: LegiScan (CC BY 4.0)</a>
            </div>
          </div>

        </div>

        <div className="footer-bottom">
          <div className="footer-copy">© {new Date().getFullYear()} WhipCount™ · All Rights Reserved · whipcount.us</div>
          <div className="footer-bottom-links">
            <button onClick={() => setShowPrivacy(true)}>Privacy Policy</button>
            <span className="footer-sep">|</span>
            <button onClick={() => setShowTos(true)}>Terms of Service</button>
            <span className="footer-sep">|</span>
            <a href="mailto:admin@whipcount.us">admin@whipcount.us</a>
          </div>
        </div>

        <div className="footer-disclaimer">
          WhipCount™ is a decision-support tool and does not constitute legal, political, or lobbying advice. All predictions are probabilistic estimates derived from publicly available data and do not guarantee legislative outcomes. Users are responsible for compliance with applicable laws and regulations governing lobbying and political activity. WhipCount™ is not affiliated with the Michigan Legislature or any government entity.
        </div>
      </footer>

      {/* PRIVACY MODAL */}
      {showPrivacy && (
        <div className="hp-privacy-overlay" onClick={() => setShowPrivacy(false)}>
          <div className="hp-privacy-modal" onClick={e => e.stopPropagation()}>
            <div className="hp-privacy-head">
              <div>
                <div className="hp-privacy-title">Privacy Policy</div>
                <div className="hp-privacy-updated">Last updated: May 11, 2026</div>
              </div>
              <button className="hp-privacy-close" onClick={() => setShowPrivacy(false)}>✕ Close</button>
            </div>
            <div className="hp-privacy-body">
              <p>We value your privacy. This Privacy Policy describes how WhipCount™ (<strong>whipcount.us</strong>) collects, uses, and protects your personal information. By accessing or using the Website, you agree to this Policy.</p>

              <h2>Information We Collect</h2>
              <p>We collect information you submit directly (name, email address) and information gathered automatically through your use of the Website (IP address, device identifiers, online identifiers, and interaction data captured via cookies and activity tracking).</p>

              <h2>How We Use Your Information</h2>
              <p>We use collected information to provide and operate the platform, manage your account, send service-related communications, detect and resolve security incidents, protect against fraudulent or malicious activity, enforce our Terms of Service, and improve the platform through analytics.</p>

              <h2>Sharing Your Information</h2>
              <p>We share your information only with trusted service providers necessary to operate the platform, including our hosting provider, authentication and database infrastructure (Supabase), email delivery service (Resend), analytics tools, and security vendors. We share information only as needed to provide services, fix errors, detect security incidents, and improve the platform.</p>

              <h2>We Do Not Sell Your Data</h2>
              <p>WhipCount™ does not sell your personal information to any third party.</p>

              <h2>How We Protect Your Information</h2>
              <p>We use SSL/TLS encryption for all data in transit, encrypt data at rest, collect only the minimum information necessary, and maintain up-to-date software and security safeguards.</p>

              <h2>Cookies</h2>
              <p>This Website uses cookies to support authentication, session management, and analytics. You may adjust cookie settings through your browser preferences.</p>

              <h2>Children's Privacy</h2>
              <p>This Website is intended for adult professional use. We do not knowingly collect personal information from children under 18.</p>

              <h2>Your Rights</h2>
              <ul>
                <li>The right to access the personal information we hold about you.</li>
                <li>The right to correct inaccurate or incomplete information.</li>
                <li>The right to request deletion of your personal information.</li>
                <li>The right to withdraw consent to our processing of your data.</li>
                <li>The right to opt out of automated decision making.</li>
              </ul>
              <p>To exercise any of these rights, contact us at <a href="mailto:admin@whipcount.us">admin@whipcount.us</a>. We will respond within 30 days.</p>

              <h2>Third-Party Links</h2>
              <p>This Website may contain links to third-party sites. We are not responsible for their content or privacy practices.</p>

              <h2>Data Storage</h2>
              <p>Your information is stored and processed in the United States.</p>

              <h2>Changes to This Policy</h2>
              <p>We may update this Privacy Policy at any time. Changes will be posted on this page with an updated date.</p>

              <h2>Contact</h2>
              <p><strong>Mason Doerr</strong> · WhipCount™<br /><a href="mailto:admin@whipcount.us">admin@whipcount.us</a> · whipcount.us</p>

              <div className="hp-privacy-disclaimer">
                <strong>Legal Disclaimer:</strong> WhipCount™ is not affiliated with the Michigan Legislature or any government agency. Legislative data is sourced from LegiScan (legiscan.com) under CC BY 4.0.
              </div>
            </div>
            <div className="hp-privacy-foot">
              <button onClick={() => setShowPrivacy(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showTos && (
        <div className="hp-privacy-overlay" onClick={() => setShowTos(false)}>
          <div className="hp-privacy-modal" onClick={e => e.stopPropagation()}>
            <div className="hp-privacy-head">
              <div>
                <div className="hp-privacy-title">Terms of Service</div>
                <div className="hp-privacy-updated">Effective Date: May 11, 2026</div>
              </div>
              <button className="hp-privacy-close" onClick={() => setShowTos(false)}>✕ Close</button>
            </div>
            <div className="hp-privacy-body">

              <p>Welcome to WhipCount™. These Terms of Service (“Terms”) govern your access to and use of the WhipCount™ platform at whipcount.us. By using the Service, you agree to these Terms.</p>
              <h2>1. Description of the Service</h2>
              <p>WhipCount™ is a legislative intelligence and decision-support platform. It is not a law firm, lobbying firm, or government agency and does not provide legal, lobbying, political, or compliance advice. Users are solely responsible for verifying all information before relying on it.</p>
              <h2>2. Acceptance of Terms</h2>
              <p>By creating an account or using the Service you represent that you are at least 18, have legal authority to enter these Terms, will comply with applicable laws, and will use the Service only for lawful purposes.</p>
              <h2>3. Account Registration</h2>
              <p>You agree to provide accurate registration information and accept responsibility for all activity under your account. WhipCount™ reserves the right to approve, deny, suspend, or terminate any account at its sole discretion.</p>
              <h2>4. Acceptable Use</h2>
              <p>You agree not to: use the Service for unlawful purposes; reverse engineer or scrape the platform; use bots without written authorization; resell platform content; interfere with platform security; or use the Service to build a competing product.</p>
              <h2>5. Legislative Data Disclaimer</h2>
              <p>Legislative data may contain delays, omissions, or inaccuracies. Users must independently verify information through official sources before taking action.</p>
              <h2>6. AI-Generated Content Disclaimer</h2>
              <p>AI-generated outputs may contain inaccuracies or hallucinations and do not constitute legal, political, lobbying, or regulatory advice. Users assume full responsibility for evaluating AI-generated content.</p>
              <h2>7. Intellectual Property</h2>
              <p>The Service and all associated software, branding, and content are owned by WhipCount™ or its licensors. You are granted a limited, non-exclusive, revocable license for authorized internal business use only.</p>
              <h2>8. User Content</h2>
              <p>You retain ownership of content you submit. By submitting content you grant WhipCount™ a license to host and process it solely to operate the Service.</p>
              <h2>9. Subscription and Billing</h2>
              <p>Paid features require a subscription. Fees are billed in advance and are non-refundable unless otherwise stated. WhipCount™ may modify pricing with reasonable notice.</p>
              <h2>10. Termination</h2>
              <p>WhipCount™ may suspend or terminate access at any time for violations of these Terms, security concerns, non-payment, or conduct harmful to the platform or its users.</p>
              <h2>11. Disclaimer of Warranties</h2>
              <p>THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WHIPCOUNT™ DISCLAIMS ALL WARRANTIES INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, AND AVAILABILITY.</p>
              <h2>12. Limitation of Liability</h2>
              <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, WHIPCOUNT™ SHALL NOT BE LIABLE FOR ANY INDIRECT OR CONSEQUENTIAL DAMAGES. TOTAL LIABILITY SHALL NOT EXCEED AMOUNTS PAID BY YOU IN THE TWELVE MONTHS PRECEDING THE CLAIM.</p>
              <h2>13. Indemnification</h2>
              <p>You agree to defend and hold harmless WhipCount™ from claims arising from your use of the Service or violation of these Terms.</p>
              <h2>14. Third-Party Services</h2>
              <p>The Service may rely on third-party providers. WhipCount™ is not responsible for third-party systems, uptime, or content.</p>
              <h2>15. Dispute Resolution and Arbitration</h2>
              <p>Before initiating any formal dispute, contact WhipCount™ at <a href="mailto:admin@whipcount.us">admin@whipcount.us</a> and attempt informal resolution for at least 30 days. If unresolved, disputes shall be resolved by binding individual arbitration under AAA Commercial Arbitration Rules in Michigan. <strong>Both parties waive the right to a jury trial and to participate in any class action.</strong> Either party may seek injunctive relief in court to prevent irreparable harm.</p>
              <h2>16. Governing Law</h2>
              <p>These Terms are governed by the laws of the State of Michigan. Legal proceedings shall be brought exclusively in Michigan courts.</p>
              <h2>17. Changes to These Terms</h2>
              <p>We may update these Terms at any time. Continued use after changes are posted constitutes acceptance.</p>
              <h2>18. Legal Disclaimer</h2>
              <p>WhipCount™ is not affiliated with the Michigan Legislature or any governmental agency. Legislative data may be sourced from LegiScan under CC BY 4.0.</p>
              <h2>Contact</h2>
              <p><strong>Mason Doerr</strong> · WhipCount™<br/><a href="mailto:admin@whipcount.us">admin@whipcount.us</a> · whipcount.us</p>
            </div>
            <div className="hp-privacy-foot">
              <button onClick={() => setShowTos(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
